import { useMemo } from 'react'

import { useFlag } from '@b-stock/fe-core/bstock-next/helpers'
import {
  activeSellerSubscriptionServiceQuery,
  getSellerCollectsConfig,
  type OrchestratedAuctionListingDetails,
  type OrchestratedOfferingListingDetails,
} from '@b-stock/fe-core/bstock-next/queries'
import { useIsPast, parseOptionalISODate } from '@b-stock/fe-core/bstock-react'
import { ListingEnums } from '@b-stock/fe-core/locale'
import {
  ItemConditionDtoItemConditionsEnum,
  ListingDtoListingTypeEnum,
  ListingDtoPricingStrategyEnum,
  StoreFrontOptionsSellerCollectsDtoShippingArrangedByEnum,
} from '@b-stock/listing-api-client'
import { SoldAs } from '@b-stock/order-api-client'

import getIsWithinBuyNowWindow from '@/buyer-portal/helpers/auctions/getIsWithinBuyNowWindow'
import { isMobileDeviceLot } from '@/buyer-portal/helpers/lot/categories'
import { useDisplayCondition } from '@/buyer-portal/helpers/lot/conditions'
import { getLotInventoryTypes } from '@/buyer-portal/helpers/lot/inventoryTypes'
import useIsLoggedIn from '@/buyer-portal/helpers/useIsLoggedIn'
import { FeatureFlags } from '@/helpers/featureFlags'

type ListingTagsListing =
  | OrchestratedOfferingListingDetails
  | OrchestratedAuctionListingDetails

/**
 * Derives the tag data shared by both `ListingTags` (legacy) and
 * `ListingTagsV2` (redesign). Each component picks its own presentation; this
 * hook only computes what to show. It reads the Seller Collects flag for one
 * reason only: to keep an auth-required query from firing for a tag that the
 * flag prevents rendering.
 *
 * The one exception is the seller's subscription, fetched here rather than in
 * each component so the two surfaces cannot disagree about the fee posture.
 */
export const useListingTagsModel = (listing: ListingTagsListing) => {
  const { listingType, pricingStrategy } = listing
  const inventoryTypes = getLotInventoryTypes(listing.lot)
  const displayCondition = useDisplayCondition(listing.lot)
  const unspecifiedCondition = ListingEnums.ItemCondition.useFormatted(
    ItemConditionDtoItemConditionsEnum.Unspecified
  )
  const isSalvageCondition = displayCondition?.toLowerCase() === 'salvage'
  const isUnspecifiedMobileCondition =
    displayCondition === unspecifiedCondition && isMobileDeviceLot(listing.lot)
  const condition =
    isSalvageCondition || isUnspecifiedMobileCondition
      ? undefined
      : displayCondition

  // We don't care about the actual result from `useIsPast` here, only that we
  // re-render when we switch from "buyNow ends in the future" to "buyNow
  // ended in the past". `getIsWithinBuyNowWindow` handles the full logic.
  useIsPast(parseOptionalISODate(listing.auction?.buyNow?.endTime))

  const isWithinBuyNowWindow = listing.auction
    ? getIsWithinBuyNowWindow(listing.auction)
    : false

  // Check if listing was sold via Buy Now using three sources:
  // 1. soldAs from order data (authoritative for sellers/admins)
  // 2. bid.buyNowBid (fallback for buyers viewing their own Buy Now purchase)
  // 3. isWithinBuyNowWindow (for active listings still in Buy Now window)
  const isBuyNow =
    listing.soldAs === SoldAs.BuyNow ||
    listing.auction?.bid?.buyNowBid === true ||
    isWithinBuyNowWindow

  const showListingTypeTag = useMemo(() => {
    return (
      listingType === ListingDtoListingTypeEnum.Contract ||
      listingType === ListingDtoListingTypeEnum.Spot ||
      listingType === ListingDtoListingTypeEnum.Fulfillment
    )
  }, [listingType])

  const showPricingStrategyTag = useMemo(() => {
    return (
      (pricingStrategy === ListingDtoPricingStrategyEnum.Auction &&
        !isBuyNow) ||
      pricingStrategy === ListingDtoPricingStrategyEnum.MakeAnOffer
    )
  }, [pricingStrategy, isBuyNow])

  const isSellerCollects = listing.sellerCollects === true
  const isLoggedIn = useIsLoggedIn()
  const sellerCollectsEnabled = useFlag(
    FeatureFlags.SELLER_COLLECTS_BUYER_EXPERIENCE
  )
  // Only Seller Collects listings need the seller's plan, and only to tell a
  // fee-bearing storefront from a fee-free one. The query requires auth, so a
  // signed-out visitor never fetches it — the tag then falls back to its
  // "unknownFee" explainer instead of crashing the anonymous render. The flag
  // is here too: with it off the tag never renders, so the call is wasted.
  const canResolvePremium =
    sellerCollectsEnabled && isSellerCollects && isLoggedIn
  const {
    data: sellerSubscription,
    isLoading: isSubscriptionLoading,
    isError: isSubscriptionError,
  } = activeSellerSubscriptionServiceQuery.useQuery(listing.accountId, {
    enabled: canResolvePremium,
  })

  const sellerCollectsConfig = getSellerCollectsConfig(listing)

  return {
    listingType,
    pricingStrategy,
    inventoryTypes,
    condition,
    isBuyNow,
    showListingTypeTag,
    showPricingStrategyTag,
    isSellerCollects,
    sellerName:
      listing.seller.storefront?.name ??
      listing.seller.account.displayName ??
      undefined,
    // Undefined until the plan resolves, and whenever it can never resolve —
    // a disabled query is not "loading", so without this the explainer would
    // claim a fee-free seller rather than making no claim at all. An errored
    // lookup is equally unknown: `?? 0` would otherwise read a failed request
    // as a seller who charges nothing.
    hasBuyerPremium:
      canResolvePremium && !isSubscriptionLoading && !isSubscriptionError
        ? (sellerSubscription?.buyersPremium ?? 0) > 0
        : undefined,
    /**
     * Whether `hasBuyerPremium` is final. False only while the plan is in
     * flight: a query that cannot run, or that errored, is settled at
     * "unknown" and a caller waiting on the value alone would wait for ever.
     */
    isBuyerPremiumSettled: !canResolvePremium || !isSubscriptionLoading,
    // Only a seller who delivers is being paid for shipping; freight booked
    // through B-Stock is billed by B-Stock, not folded into the seller payment.
    sellerArrangesShipping:
      sellerCollectsConfig?.shippingArrangedBy ===
      StoreFrontOptionsSellerCollectsDtoShippingArrangedByEnum.SellerDelivers,
  }
}
